// src/pages/Admin/Vendors/VendorProducts.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { adminAPI } from '../../../api/admin';
import ProductFormModal from '../../../components/products/ProductFormModal';
import ProductList from '../../../components/products/ProductList';
import type { Product, ProductVariant, Warehouse } from '../../../types';
import StockAdjustmentModal from '../../../components/admin/StockAdjustmentModal';
import { stockAPI } from '../../../api/stock';
import { useProductFilters } from '../../../hooks/useProductFilters';
import toast from '../../../utils/toast';

interface VendorContext {
    vendor: any;
    refresh: () => void;
}

const VendorProducts: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const context = useOutletContext<VendorContext>();
    const vendor = context?.vendor;
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<any>(null);
    const [reviewMode, setReviewMode] = useState<'view' | 'review'>('review');
    const [stockModal, setStockModal] = useState<{
        isOpen: boolean;
        product: Product | null;
        variants: ProductVariant[];
    }>({
        isOpen: false,
        product: null,
        variants: []
    });
    const {
        products,
        loading,
        pagination,
        statusFilter,
        searchTerm,
        handleStatusFilterChange,
        handleSearchChange,
        handlePageChange,
        fetchData,
        setProducts,
    } = useProductFilters({
        fetchProducts: async (page, status, search) => {
            const params: any = {
                page: page,
                page_size: 20,
                vendor: id,
            };

            // ✅ Admin can see all statuses including archived
            if (status === 'archived') {
                params.status = 'archived';
            } else if (status !== 'all' && status !== 'active') {
                params.status = status;
            }

            if (search?.trim()) {
                params.search = search.trim();
            }

            // ✅ Admin doesn't need vendor filter (shows all vendors)
            const response = await adminAPI.getProducts(params);
            return {
                data: response.data?.results || response.data || [],
                total: response.data?.count || 0,
                pages: Math.ceil((response.data?.count || 0) / 20),
            };
        },
        initialStatus: 'all',
    });


    const statusOptions = [
        { value: 'all', label: 'All' },
        { value: 'draft', label: 'Draft' },
        { value: 'pending_review', label: 'Pending Review' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'changes_requested', label: 'Changes Requested' },
    ];

    // useEffect(() => {
    //     fetchProducts(1);
    //     console.log("vendpro")
    // }, [id, statusFilter]);

    useEffect(() => {
        const fetchWarehouses = async () => {
            const response = await stockAPI.getWarehouses();
            if (!response.error) {
                setWarehouses(response.data || []);
            }
        };
        fetchWarehouses();
    }, []);

    // const fetchProducts = async (newPage?: number) => {
    //     setLoading(true);
    //     try {
    //         const currentPage = newPage || page;
    //         const response = await adminAPI.getVendorProducts(id!, {
    //             page: currentPage,
    //             page_size: pagination.pageSize,
    //             status: statusFilter !== 'all' ? statusFilter : undefined,
    //         });

    //         console.log('📦 Vendor Products Response:', response.data);

    //         if (response.data) {
    //             const productData = Array.isArray(response.data)
    //                 ? response.data
    //                 : (response.data as any).results || [];

    //             setProducts(productData);

    //             // Handle pagination
    //             const total = (response.data as any).count || productData.length;
    //             const totalPages = Math.ceil(total / pagination.pageSize);

    //             setPagination({
    //                 page: currentPage,
    //                 total: total,
    //                 pages: totalPages,
    //                 pageSize: pagination.pageSize,
    //             });
    //         }
    //     } catch (error) {
    //         showToast('Failed to load products', 'error');
    //     } finally {
    //         setLoading(false);
    //     }
    // };
    // const fetchProducts = async (newPage?: number, filter?: string) => {
    //     setLoading(true);
    //     try {
    //         const currentPage = newPage || page;
    //         const currentFilter = filter !== undefined ? filter : statusFilter;

    //         const response = await adminAPI.getVendorProducts(id!, {
    //             page: currentPage,
    //             page_size: pagination.pageSize,
    //             status: currentFilter !== 'all' ? currentFilter : undefined,
    //         });

    //         if (response.data) {
    //             const productData = Array.isArray(response.data)
    //                 ? response.data
    //                 : (response.data as any).results || [];

    //             setProducts(productData);

    //             // ✅ Use the count from the API response
    //             const total = (response.data as any).count || productData.length;
    //             const totalPages = Math.ceil(total / pagination.pageSize);

    //             setPagination({
    //                 page: currentPage,
    //                 total: total,
    //                 pages: totalPages,
    //                 pageSize: pagination.pageSize,
    //             });
    //         }
    //     } catch (error) {
    //         showToast('Failed to load products', 'error');
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    // const handleStatusFilterChange = (filter: string) => {
    //     setStatusFilter(filter);
    //     // setPage(1); // Reset to first page
    //     // fetchProducts(1, filter); // Refetch with new filter
    // };

    // const handlePageChange = (newPage: number) => {
    //     setPage(newPage);
    //     fetchProducts(newPage);
    // };
const handleVendorProductRowClick = async (product: any) => {
    try {
        // ✅ Fetch the latest product data BEFORE opening modal
        const response = await adminAPI.getProduct(product.id);
        const updatedProduct = response.data;
        
        // ✅ Update the product in the list
        setProducts(prevProducts =>
            prevProducts.map(p =>
                p.id === updatedProduct.id ? updatedProduct : p
            )
        );
        
        // ✅ Determine mode based on status
        const mode = updatedProduct.status === 'pending_review' ? 'review' : 'view';
        
        // ✅ Now open the modal with the updated product
        setSelectedProduct(updatedProduct);
        setReviewMode(mode);
        setShowReviewModal(true);
    } catch (error) {
        console.error('Failed to fetch product:', error);
        toast.error('Failed to load product details');
        // Fallback to existing product if fetch fails
        const mode = product.status === 'pending_review' ? 'review' : 'view';
        setSelectedProduct(product);
        setReviewMode(mode);
        setShowReviewModal(true);
    }
};
    return (
        <div className="space-y-4">
            {/* Products Table */}
            <ProductList
                products={products}
                loading={loading}
                onRefresh={fetchData}
                showVendorColumn={false}
                actions={['view', 'review']}
                showAddButton={false}
                onProductView={(product) => {
                    setSelectedProduct(product);
                    setReviewMode('view');
                    setShowReviewModal(true);
                }}
                onProductReview={(product) => {
                    setSelectedProduct(product);
                    setReviewMode('review');
                    setShowReviewModal(true);
                }}
                onStockAdjust={(product) => {
                    setStockModal({ isOpen: true, product });
                }}
                onRowClick={handleVendorProductRowClick}
                title={`${vendor?.business_name} - Products`}
                emptyMessage="This vendor has no products yet"
                pagination={pagination}
                onPageChange={handlePageChange}
                onFilterChange={handleStatusFilterChange}
                isStaff={true}
                onSearchChange={handleSearchChange}
                statusFilter={statusFilter}
            />
            {showReviewModal && selectedProduct && (
                <ProductFormModal
                    product={selectedProduct}
                    onClose={() => {
                        setShowReviewModal(false);
                        setSelectedProduct(null);
                    }}
                    onSuccess={() => {
                        fetchData();
                        setShowReviewModal(false);
                        setSelectedProduct(null);
                    }}
                    mode={reviewMode}
                    isStaff={true}
                />
            )}

            {stockModal.isOpen &&
                <StockAdjustmentModal
                    isOpen={stockModal.isOpen}
                    onClose={() => setStockModal({ isOpen: false, product: null, variants: [] })}
                    onSuccess={fetchData} // Refresh your product list
                    product={stockModal.product!}
                    variants={stockModal.variants}
                    warehouses={warehouses}
                />
            }
        </div>
    );
};

export default VendorProducts;